// Forgd V2 Tailwind Tokens
// This file is kept separate from tailwind.config.ts to avoid perf issues when importing

export const colors = {
  neutral: {
    100: '#FFFFFF',
    200: '#F2F2F1',
    300: '#EDEDEA',
    400: '#E1E1DC',
    500: '#D5D5CE',
    600: '#C7C7BE',
    700: '#8E8E7F',
    800: '#696954',
    900: '#434329',
    950: '#494943',
  },
  gray: {
    100: '#F3F4F6',
    200: '#E5E7EB',
    300: '#D1D5DB',
    400: '#9CA3AF',
    500: '#606A7F',
    600: '#3F4D62',
    700: '#2C3B52',
    800: '#1A293B',
    900: '#041524',
    950: '#010D17',
  },
  blue: {
    100: '#EAF2FB',
    200: '#C6DEFB',
    300: '#ADCCF8',
    400: '#76A9FA',
    500: '#2D73FF',
    600: '#1C64F2',
    700: '#1A56DB',
    800: '#1045AD',
    900: '#05296D',
    950: '#041A44',
  },
  green: {
    100: '#DFE9E3',
    200: '#C6DAD3',
    300: '#ABCEC0',
    400: '#68B095',
    500: '#0E9F6E',
    600: '#057A55',
    700: '#046C4E',
    800: '#03543F',
    900: '#014737',
    950: '#003126',
  },
  red: {
    100: '#F3E5E5',
    200: '#F4D6D6',
    300: '#EEB9B9',
    400: '#F98080',
    500: '#F05252',
    600: '#E02424',
    700: '#C81E1E',
    800: '#9B1C1C',
    900: '#771D1D',
    950: '#4D0A0A',
  },
  yellow: {
    50: '#EAE8D1',
    100: '#EAE8D1',
    200: '#DCD8A7',
    300: '#D5CD7D',
    400: '#C4BB5F',
    500: '#A79D38',
    600: '#918619',
    700: '#7D730F',
    800: '#675E06',
    900: '#544D00',
    950: '#3B3600',
  },
  brand: {
    100: '#2D73FF',
    200: '#2362DB',
    300: '#1A54C4',
    400: '#1045AD',
    500: '#06348D',
    600: '#05296D',
    700: '#052157',
    800: '#021842',
    900: '#030021',
  },
}

export const heatmap = {
  0: '#EEB9B9',
  10: '#E8C3B3',
  20: '#E2CEAD',
  30: '#DCD8A7',
  40: '#D5D7AB',
  50: '#CED5AE',
  60: '#C7D4B2',
  70: '#C0D2B5',
  80: '#B9D1B9',
  90: '#B2CFBC',
  100: '#ABCEC0',
}

export const heatmapStrong = {
  0: '#B23A38',
  10: '#B55439',
  20: '#B96D39',
  30: '#BC873A',
  40: '#C0A03A',
  50: '#C3BA3B',
  60: '#9FB242',
  70: '#7CAA49',
  80: '#58A351',
  90: '#359B58',
  100: '#11935F',
}

// Figma `chart-greenHeatmap/*` is a linear ramp — the step number IS the alpha
// percentage. The suffixes below are HEX alpha bytes, not decimal percentages;
// the previous values (`}10`, `}20`, …) read as 6%, 13%, … and were ~40% too light.
export const greenHeatmap = {
  0: `${colors.green['300']}00`,
  10: `${colors.green['300']}1A`,
  20: `${colors.green['300']}33`,
  30: `${colors.green['300']}4D`,
  40: `${colors.green['300']}66`,
  50: `${colors.green['300']}80`,
  60: `${colors.green['300']}99`,
  70: `${colors.green['300']}B2`,
  80: `${colors.green['300']}CC`,
  90: `${colors.green['300']}E5`,
  100: colors.green['300'],
}

export const backgroundColor = {
  page: colors.neutral['200'],
  light: colors.neutral['100'],
  secondary: colors.neutral['300'],
  tertiary: `${colors.neutral['600']}4D`, // 30% — Figma background/tertiary (hex alpha, was 0x30 = 19%)
  dark: colors.brand['900'],
  // Interactive elements
  solid: colors.brand['900'],
  'solid-hover': colors.brand['500'],
  accent: colors.brand['300'],
  'accent-hover': colors.brand['500'],
  'accent-green': colors.green['600'],
  'accent-green-hover': colors.green['800'],
  'outlined-hover': `${colors.neutral['600']}4D`, // 30% — Figma background/outlined-hover
  'negative-hover': `${colors.brand['500']}30`,
  // Feedback backgrounds
  warning: colors.yellow['100'],
  success: colors.green['100'],
  error: colors.red['100'],
  disabled: `${colors.neutral['900']}4D`, // 30% opacity
  // Transparent feedback backgrounds (10% opacity)
  'accent-transparent': `${colors.brand['300']}1A`,
  'secondary-transparent': `${colors.gray['600']}1A`,
  'warning-transparent': `${colors.yellow['700']}1A`,
  'success-transparent': `${colors.green['600']}1A`,
  'error-transparent': `${colors.red['600']}1A`,
  ...Object.fromEntries(
    Object.entries(heatmap).map(([key, value]) => {
      return [`heatmap-${key}`, value]
    }),
  ),
  ...Object.fromEntries(
    Object.entries(greenHeatmap).map(([key, value]) => {
      return [`green-heatmap-${key}`, value]
    }),
  ),
  ...Object.fromEntries(
    Object.entries(heatmapStrong).map(([key, value]) => {
      return [`heatmap-strong-${key}`, value]
    }),
  ),
}

export const borderColor = {
  default: colors.neutral['600'],
  'default-negative': colors.gray['700'],
  secondary: colors.gray['600'],
  hard: colors.brand['900'],
  white: colors.neutral['100'],
  accent: colors.brand['300'],
  muted: colors.neutral['700'],
  // Interactive borders
  outlined: `${colors.neutral['900']}30`,
  'outlined-hover': `${colors.neutral['900']}60`,
  'negative-hover': colors.brand['100'],
  focus: colors.brand['100'],
  warning: colors.yellow['700'],
  success: colors.green['600'],
  error: colors.red['600'],
  disabled: `${colors.neutral['600']}60`,
  // Transparent feedback borders
  'accent-transparent': `${colors.brand['300']}30`,
  'secondary-transparent': `${colors.gray['600']}30`,
  'neutral-transparent': `${colors.neutral['600']}60`,
  'warning-transparent': `${colors.yellow['700']}30`,
  'success-transparent': `${colors.green['600']}30`,
  'error-transparent': `${colors.red['600']}30`,
}

export const textColor = {
  primal: colors.brand['900'],
  'primal-negative': colors.neutral['100'],
  'primal-inverted': colors.neutral['100'],
  secondary: colors.gray['600'],
  'secondary-negative': colors.gray['400'],
  'secondary-inverted': colors.gray['400'],
  hyperlink: colors.brand['300'],
  'hyperlink-hover': colors.brand['500'],
  accent: colors.brand['300'],
  'accent-negative': colors.brand['100'],
  'accent-inverted': colors.brand['100'], // Figma text/accent-inverted
  muted: colors.neutral['700'],
  placeholder: `${colors.neutral['900']}99`, // 60% — Figma text/placeholder + icon/placeholder
  'chart-label': `${colors.neutral['900']}99`, // 60% opacity
  warning: colors.yellow['700'],
  'warning-negative': colors.yellow['400'],
  success: colors.green['600'],
  'success-negative': colors.green['500'],
  error: colors.red['600'],
  'error-negative': colors.red['500'],
  disable: `${colors.neutral['900']}30`,
}

export const fontSize = {
  // @v2 - Updated font sizes matching Figma UIKit
  '2xs': ['0.6875rem', { lineHeight: '1.5' }],
  xs: ['0.75rem', { lineHeight: '1.5' }],
  sm: ['0.875rem', { lineHeight: '1.5' }],
  md: ['1rem', { lineHeight: '1.5' }],
  lg: ['1.125rem', { lineHeight: '1.5' }],
  xl: ['1.25rem', { lineHeight: '1.5' }],
  '2xl': ['1.5rem', { lineHeight: '1.5' }],
  '3xl': ['1.875rem', { lineHeight: '1.2' }],
  '4xl': ['2.25rem', { lineHeight: '1.2' }],
  '5xl': ['3rem', { lineHeight: '1.2' }],
  '6xl': ['3.75rem', { lineHeight: '1.2' }],
  '7xl': ['4.5rem', { lineHeight: '1.2' }],
  // @v2 - Custom sizes for KPIs and mono
  'kpi-xs': ['0.75rem', { lineHeight: '1', fontWeight: '600', letterSpacing: '-0.015em' }],
  'kpi-sm': ['0.875rem', { lineHeight: '1', fontWeight: '600', letterSpacing: '-0.015em' }],
  'kpi-md': ['1rem', { lineHeight: '1', fontWeight: '600', letterSpacing: '-0.015em' }],
  'kpi-lg': ['1.125rem', { lineHeight: '1', fontWeight: '600', letterSpacing: '-0.015em' }],
  'kpi-xl': ['1.25rem', { lineHeight: '1', fontWeight: '600', letterSpacing: '-0.015em' }],
  'kpi-2xl': ['1.5rem', { lineHeight: '1', fontWeight: '600', letterSpacing: '-0.015em' }],
  'mono-xs': ['0.625rem', { lineHeight: '1', letterSpacing: '-0.015em' }],
  'mono-sm': ['0.75rem', { lineHeight: '1', letterSpacing: '-0.015em' }],
  'mono-md': ['0.813rem', { lineHeight: '1', letterSpacing: '-0.015em' }],
  'mono-lg': ['0.875rem', { lineHeight: '1', letterSpacing: '-0.015em' }],
  'mono-xl': ['1rem', { lineHeight: '1', letterSpacing: '-0.015em' }],
}

export const letterSpacing = {
  snug: '-0.015em',
}
